import { useState, useEffect } from 'react';
import { statsApi } from '../api';
import type { DashboardStats } from '../types';
import { FileText, Clock, Shield, Users, TrendingUp } from 'lucide-react';
import { Header, StatsCard, Skeleton } from '../components/ui';
import { fmtMs } from '../utils';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend,
} from 'recharts';

// ── Tooltip personnalisé pour les barres ──────────────────────────

function CustomBarTooltip({ active, payload }: { active?: boolean; payload?: { value: number; payload: { unit?: string } }[] }) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800">
      {item.value}{item.payload.unit ?? ''}
    </div>
  );
}

// ── Tooltip personnalisé pour le pie ─────────────────────────────

function CustomPieTooltip({ active, payload }: { active?: boolean; payload?: { name: string; value: number }[] }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-gray-100 shadow-lg rounded-xl px-4 py-2.5 text-sm font-semibold text-gray-800">
      {payload[0].name} : <span className="text-gray-600">{payload[0].value}</span>
    </div>
  );
}


// ── Page ─────────────────────────────────────────────────────────

export function StatsPage() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    statsApi.dashboard()
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const total         = stats?.total_documents   ?? 0;
  const alertes       = stats?.active_alerts     ?? 0;
  const conformes     = Math.max(total - alertes, 0);
  const conformityPct = total > 0 ? Math.round((conformes / total) * 100) : 0;
  const classPct      = Math.round((stats?.avg_classification_confidence ?? 0) * 100);

  // ── Données recharts ──────────────────────────────────────────

  const barData = [
    { name: 'Précision IA',     value: classPct,      unit: '%', fill: '#0BBCC1' },
    { name: 'Conformité RGPD',  value: conformityPct, unit: '%', fill: '#10b981' },
    { name: "Docs aujourd'hui", value: stats?.documents_today ?? 0, unit: '', fill: '#7c3aed' },
  ];

  const pieData = [
    { name: 'Conformes', value: conformes, fill: '#10b981' },
    { name: 'Alertes',   value: alertes,   fill: '#f59e0b' },
  ];

  return (
    <div className="flex-1 overflow-y-auto">
      <Header title="Statistiques" />
      <div className="p-4 sm:p-6 space-y-6">

        {/* ── KPI cards ─────────────────────────────────────────── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-28" />)}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatsCard
              label="Documents traités"
              value={total}
              sub={`↑ ${stats?.documents_today ?? 0} aujourd'hui`}
              icon={FileText} iconBg="bg-blue-50" iconColor="text-blue-600" accent="text-emerald-600"
            />
            <StatsCard
              label="Collaborateurs"
              value={stats?.total_collaborateurs ?? 0}
              icon={Users} iconBg="bg-violet-50" iconColor="text-violet-600"
            />
            <StatsCard
              label="Temps moyen"
              value={stats ? fmtMs(stats.avg_processing_time_ms) : '—'}
              sub="par document"
              icon={Clock} iconBg="bg-amber-50" iconColor="text-amber-600"
            />
            <StatsCard
              label="Taux de conformité"
              value={`${conformityPct}%`}
              sub={`${alertes} alerte${alertes !== 1 ? 's' : ''} active${alertes !== 1 ? 's' : ''}`}
              icon={Shield} iconBg="bg-teal-50" iconColor="text-teal-600"
            />
          </div>
        )}

        {/* ── Graphiques ────────────────────────────────────────── */}
        {loading ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Skeleton className="h-72" />
            <Skeleton className="h-72" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

            {/* ── BarChart — Performance IA ──────────────────────── */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-5">
                <TrendingUp size={18} className="text-gray-400" strokeWidth={1.6} />
                <h3 className="font-bold text-gray-900">Performance IA</h3>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={barData} barSize={40} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: 500 }}
                    axisLine={false} tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 11, fill: '#9ca3af' }}
                    axisLine={false} tickLine={false}
                    domain={[0, 100]}
                  />
                  <Tooltip content={<CustomBarTooltip />} cursor={{ fill: '#f9fafb', radius: 8 }} />
                  <Bar
                    dataKey="value"
                    radius={[8, 8, 0, 0]}
                    isAnimationActive
                    animationDuration={900}
                    animationEasing="ease-out"
                  >
                    {barData.map((entry, i) => <Cell key={i} fill={entry.fill} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>

            {/* ── PieChart — État des documents ─────────────────── */}
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-6">
              <div className="flex items-center gap-2 mb-5">
                <Shield size={18} className="text-gray-400" strokeWidth={1.6} />
                <h3 className="font-bold text-gray-900">État des documents</h3>
              </div>
              <div className="relative">
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie
                      data={total === 0 ? [{ name: 'Aucun document', value: 1, fill: '#e5e7eb' }] : pieData}
                      cx="50%" cy="50%"
                      innerRadius={62} outerRadius={90}
                      paddingAngle={total === 0 ? 0 : 4}
                      dataKey="value"
                      isAnimationActive
                      animationDuration={900}
                      animationEasing="ease-out"
                      labelLine={false}
                    >
                      {(total === 0 ? [{ fill: '#e5e7eb' }] : pieData).map((entry, i) => (
                        <Cell key={i} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomPieTooltip />} />
                    <Legend
                      iconType="circle" iconSize={10}
                      formatter={(value) => (
                        <span style={{ fontSize: 12, color: '#4b5563', fontWeight: 600 }}>{value}</span>
                      )}
                    />
                  </PieChart>
                </ResponsiveContainer>
                {/* Label central du donut — overlay CSS */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none" style={{ paddingBottom: 32 }}>
                  <div className="text-center">
                    <p className="text-2xl font-extrabold text-gray-900 leading-none">{total}</p>
                    <p className="text-xs text-gray-400 mt-1">total</p>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}

        {/* ── Indicateurs synthèse ──────────────────────────────── */}
        {!loading && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { label: 'Précision classification',  value: classPct,      color: '#0BBCC1', bg: 'bg-teal-50',    text: 'text-teal-700'    },
              { label: 'Conformité RGPD',           value: conformityPct, color: '#10b981', bg: 'bg-emerald-50', text: 'text-emerald-700' },
              {
                label: 'Activité du jour',
                value: total > 0 ? Math.min(Math.round(((stats?.documents_today ?? 0) / total) * 100), 100) : 0,
                color: '#7c3aed',
                bg: 'bg-violet-50',
                text: 'text-violet-700',
              },
            ].map(item => (
              <div key={item.label} className={`rounded-xl border border-gray-100 shadow-sm p-5 ${item.bg} animate-fade-up`}>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">{item.label}</p>
                <span className={`text-4xl font-extrabold ${item.text}`}>{item.value}%</span>
                <div className="mt-3 h-1.5 bg-white/60 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all duration-1000"
                    style={{ width: `${item.value}%`, backgroundColor: item.color }}
                  />
                </div>
              </div>
            ))}
          </div>
        )}

      </div>
    </div>
  );
}
